import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { applyServerErrors } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authApi } from '../api';
import { registerSchema, type RegisterValues } from '../schemas';
import { AuthLayout } from '../components/AuthLayout';

export function RegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setToken = useAuthStore((s) => s.setToken);
  const setSession = useAuthStore((s) => s.setSession);

  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      business_name: '',
      name: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      country_code: 'IN',
    },
  });

  const register = useMutation({
    mutationFn: (values: RegisterValues) => authApi.register(values),
    onSuccess: (session) => {
      setToken(session.token);
      setSession(session);
      queryClient.setQueryData(['session'], session);

      // Straight into a working workspace — registration has already seeded
      // roles, an outlet, tax rates and payment methods.
      navigate('/dashboard', { replace: true });
      toast.success(`Welcome to Voltra, ${session.user.name.split(' ')[0]}`);
    },
    onError: (error) => {
      if (!applyServerErrors(error, form.setError as never)) {
        toast.error(error instanceof Error ? error.message : 'Could not create your workspace.');
      }
    },
  });

  const { errors } = form.formState;

  return (
    <AuthLayout
      title="Run the whole counter from one screen"
      subtitle="Billing, stock, customers and staff. Free for 14 days — no card, no setup fee."
      footer={
        <p className="text-sm text-ink-secondary">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-ink underline underline-offset-4 hover:text-brand">
            Sign in here
          </Link>
        </p>
      }
    >
      <form
        onSubmit={form.handleSubmit((values) => register.mutate(values))}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field
          label="Business name"
          htmlFor="business_name"
          error={errors.business_name?.message}
          required
        >
          <Input
            id="business_name"
            autoFocus
            placeholder="Corner Store"
            aria-invalid={!!errors.business_name}
            {...form.register('business_name')}
          />
        </Field>

        {/* Paired, because six stacked fields put Create Account below the
            fold on a 900px laptop — and a signup button nobody scrolls to is
            a signup nobody finishes. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" htmlFor="name" error={errors.name?.message} required>
            <Input
              id="name"
              autoComplete="name"
              aria-invalid={!!errors.name}
              {...form.register('name')}
            />
          </Field>

          <Field label="Phone" htmlFor="phone" error={errors.phone?.message} hint="Optional">
            <Input id="phone" type="tel" autoComplete="tel" {...form.register('phone')} />
          </Field>
        </div>

        <Field label="Email" htmlFor="email" error={errors.email?.message} required>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            aria-invalid={!!errors.email}
            {...form.register('email')}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Password"
            htmlFor="password"
            error={errors.password?.message}
            hint="At least 8 characters"
            required
          >
            <PasswordInput
              id="password"
              autoComplete="new-password"
              aria-invalid={!!errors.password}
              {...form.register('password')}
            />
          </Field>

          <Field
            label="Confirm"
            htmlFor="password_confirmation"
            error={errors.password_confirmation?.message}
            required
          >
            <PasswordInput
              id="password_confirmation"
              autoComplete="new-password"
              aria-invalid={!!errors.password_confirmation}
              {...form.register('password_confirmation')}
            />
          </Field>
        </div>

        <Button type="submit" size="lg" loading={register.isPending} className="mt-1 w-full">
          Create workspace
        </Button>

        <p className="text-center text-xs text-ink-muted">
          By continuing you agree to our terms and privacy policy.
        </p>
      </form>
    </AuthLayout>
  );
}
